import { useState } from "react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Sparkles, Zap, Calendar, ShieldCheck, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * In-inbox Upgrade CTA.
 *
 * REQUIREMENT: always sends the currently-viewed alias address as `customer_email`.
 * No fallback to account email or any "main" alias is permitted.
 *
 * Props:
 *   activeAliasEmail: string — the inbox address the user is currently viewing.
 */
const PLAN_OPTIONS = [
  { key: "pro_monthly", label: "Pro Monthly", caption: "Full access, cancel anytime", icon: Sparkles },
  { key: "pro_yearly", label: "Pro Yearly", caption: "Save 2 months", icon: Calendar },
  { key: "day_pass", label: "Day Pass", caption: "24 hours, one-time", icon: Clock },
  { key: "verification", label: "Verify Account", caption: "Trusted billing signal", icon: ShieldCheck },
];

export default function UpgradeMenu({ activeAliasEmail }) {
  const [busy, setBusy] = useState(null);

  const handleSelect = async (purchase_type) => {
    if (!activeAliasEmail) {
      toast.error("Open an inbox first to upgrade — the receipt goes to that exact address.");
      return;
    }
    if (busy) return; // prevent double-click
    setBusy(purchase_type);
    const payload = {
      purchase_type,
      customer_email: activeAliasEmail,
      origin_url: window.location.origin,
    };
    // eslint-disable-next-line no-console
    console.log(`[Upgrade] Checkout started for alias: ${activeAliasEmail}`, payload);
    try {
      const { data } = await api.post("/payments/v2/checkout", payload);
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
          data-testid="inbox-upgrade-button"
          disabled={!!busy}
        >
          {busy ? (
            <><Loader2 size={13} className="mr-1 animate-spin" /> Preparing…</>
          ) : (
            <><Zap size={13} className="mr-1" /> Upgrade</>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-[11px] font-normal text-[#555555]">
          Receipt will arrive in:
          <div className="font-mono text-[#111111] text-xs mt-0.5 truncate" data-testid="upgrade-menu-alias">
            {activeAliasEmail || "— no inbox selected —"}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {PLAN_OPTIONS.map(({ key, label, caption, icon: Icon }) => {
          const isBusy = busy === key;
          return (
            <DropdownMenuItem
              key={key}
              onSelect={(e) => {
                // Keep dropdown open while loading; handle click manually
                if (busy) { e.preventDefault(); return; }
                e.preventDefault();
                handleSelect(key);
              }}
              disabled={!activeAliasEmail || (!!busy && !isBusy)}
              data-testid={`upgrade-option-${key}`}
              className="flex items-start gap-2 cursor-pointer"
            >
              {isBusy ? (
                <Loader2 size={14} className="mt-0.5 text-violet-600 animate-spin" />
              ) : (
                <Icon size={14} className="mt-0.5 text-violet-600" />
              )}
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-medium text-[#111111]">{label}</span>
                <span className="text-[11px] text-[#888888]">{isBusy ? "Preparing secure checkout…" : caption}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
